import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Save, RotateCcw } from "lucide-react";
import {
  MD_POSITION_END,
  MD_POSITION_START,
  buildJDaysFromRange,
  createDefaultPercentageMatrix,
  getDayDisplayLabel,
  getDefaultMetricLabel,
  getMetricDisplayLabel,
  normalizeMetricLabelOverrides,
  normalizeDayLabelOverrides,
  normalizeMicrocycleConfig,
  normalizePercentageMatrix,
  toFiniteNumber,
} from "./weeklyRecapConfig";

const inputBase = "w-28 rounded-lg border px-3 py-2 text-sm font-semibold text-center outline-none focus:ring-2";

const CHART_MODE_ABSOLUTE = "absolute";
const CHART_MODE_PERCENT = "percent";

function getMetricKeys(metrics) {
  return (metrics || []).map((metric) => metric.key).filter(Boolean);
}

function normalizeMetricKeys(keys, metrics) {
  const allowed = new Set(getMetricKeys(metrics));
  const filtered = (keys || []).filter((key) => allowed.has(key));

  if (filtered.length > 0) return filtered;

  const fallback = metrics?.[0]?.key;
  return fallback ? [fallback] : [];
}

function normalizeDisplayConfig(config, externalMetrics, internalMetrics) {
  const allMetrics = [...(externalMetrics || []), ...(internalMetrics || [])];
  const chartDisplayMode =
    config?.chartDisplayMode === CHART_MODE_PERCENT
      ? CHART_MODE_PERCENT
      : CHART_MODE_ABSOLUTE;

  return {
    chartDisplayMode,
    externalMetricKeys: normalizeMetricKeys(config?.externalMetricKeys, externalMetrics),
    internalMetricKeys: normalizeMetricKeys(config?.internalMetricKeys, internalMetrics),
    metricLabelOverrides: normalizeMetricLabelOverrides(
      config?.metricLabelOverrides || config?.metric_label_overrides,
      allMetrics,
    ),
    dayLabelOverrides: normalizeDayLabelOverrides(
      config?.dayLabelOverrides || config?.day_label_overrides,
    ),
  };
}

function MetricSelector({
  title,
  scope,
  metrics,
  selectedKeys,
  isDarkMode,
  t,
  onToggle,
  onAll,
  onNone,
  metricLabelOverrides,
}) {
  const selected = new Set(selectedKeys || []);

  return (
    <div className={`rounded-xl border p-3 ${isDarkMode ? "border-gray-800 bg-gray-900/50" : "border-gray-200 bg-gray-50"}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-xs font-bold uppercase tracking-wider opacity-75">
          {title}
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onAll(scope)}
            className={`rounded-md px-2 py-1 text-[10px] font-bold ${
              isDarkMode ? "bg-gray-800 text-gray-300 hover:bg-gray-700" : "bg-white text-gray-600 hover:bg-gray-100"
            }`}
          >
            {t("weekly_recap.modal.select_all", { defaultValue: "All" })}
          </button>

          <button
            type="button"
            onClick={() => onNone(scope)}
            className={`rounded-md px-2 py-1 text-[10px] font-bold ${
              isDarkMode ? "bg-gray-800 text-gray-300 hover:bg-gray-700" : "bg-white text-gray-600 hover:bg-gray-100"
            }`}
          >
            {t("weekly_recap.modal.select_none", { defaultValue: "None" })}
          </button>
        </div>
      </div>

      <div className="max-h-36 space-y-1 overflow-auto pr-1">
        {(metrics || []).map((metric) => {
          const checked = selected.has(metric.key);

          return (
            <label
              key={metric.key}
              className={`flex cursor-pointer items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-xs ${
                checked
                  ? isDarkMode
                    ? "bg-cyan-950/40 text-cyan-200"
                    : "bg-cyan-50 text-cyan-700"
                  : isDarkMode
                    ? "hover:bg-gray-800 text-gray-300"
                    : "hover:bg-white text-gray-700"
              }`}
            >
              <span className="truncate font-semibold">
                {getMetricDisplayLabel(metric, t, metricLabelOverrides)}
              </span>

              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(scope, metric.key)}
                className="h-3.5 w-3.5 accent-cyan-600"
              />
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default function WeeklyRecapPlanningModal({
  isOpen,
  isDarkMode,
  metrics,
  externalMetrics = [],
  internalMetrics = [],
  jDays,
  initialMatrix,
  initialDisplayConfig,
  initialMicrocycle,
  onClose,
  onSave,
  calculateSimilarityFromDay = "MD+1",
}) {
  const { t } = useTranslation();
  const [draftMatrix, setDraftMatrix] = useState(() =>
    normalizePercentageMatrix(initialMatrix, metrics, jDays),
  );
  const [draftDisplayConfig, setDraftDisplayConfig] = useState(() =>
    normalizeDisplayConfig(initialDisplayConfig, externalMetrics, internalMetrics),
  );
  const [draftMicrocycle, setDraftMicrocycle] = useState(() =>
    normalizeMicrocycleConfig(initialMicrocycle),
  );

  const draftJDays = useMemo(() => {
    const fromRange = buildJDaysFromRange(
      draftMicrocycle?.start_offset,
      draftMicrocycle?.end_offset,
    );
    if (Array.isArray(fromRange) && fromRange.length > 0) return fromRange;
    return Array.isArray(jDays) && jDays.length > 0 ? jDays : [];
  }, [draftMicrocycle, jDays]);

  const fallbackMatrix = useMemo(
    () => createDefaultPercentageMatrix(metrics, draftJDays),
    [metrics, draftJDays],
  );

  useEffect(() => {
    if (!isOpen) return;
    const nextMicrocycle = normalizeMicrocycleConfig({
      ...(initialMicrocycle || {}),
      ...(calculateSimilarityFromDay ? { calculate_similarity_from: calculateSimilarityFromDay } : {}),
    });
    const nextJDays = buildJDaysFromRange(nextMicrocycle.start_offset, nextMicrocycle.end_offset);

    setDraftMicrocycle(nextMicrocycle);
    setDraftMatrix(normalizePercentageMatrix(initialMatrix, metrics, nextJDays));
    setDraftDisplayConfig(
      normalizeDisplayConfig(initialDisplayConfig, externalMetrics, internalMetrics),
    );
  }, [
    isOpen,
    initialMatrix,
    initialDisplayConfig,
    initialMicrocycle,
    calculateSimilarityFromDay,
    metrics,
    externalMetrics,
    internalMetrics,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    setDraftMatrix((prev) => normalizePercentageMatrix(prev, metrics, draftJDays));
  }, [isOpen, draftJDays, metrics]);

  if (!isOpen) return null;

  const setCell = (metricKey, day, rawValue) => {
    const parsed = toFiniteNumber(rawValue, 0);
    const clamped = Math.max(0, Math.min(300, parsed));

    setDraftMatrix((prev) => ({
      ...prev,
      [metricKey]: {
        ...(prev?.[metricKey] || {}),
        [day]: clamped,
      },
    }));
  };

  const resetDefaults = () => {
    setDraftMatrix(fallbackMatrix);
  };

  const setMetricLabelOverride = (metricKey, rawValue) => {
    setDraftDisplayConfig((prev) => {
      const nextOverrides = {
        ...(prev?.metricLabelOverrides || {}),
      };

      if (typeof rawValue === "string" && rawValue.trim()) {
        nextOverrides[metricKey] = rawValue;
      } else {
        delete nextOverrides[metricKey];
      }

      return normalizeDisplayConfig(
        {
          ...prev,
          metricLabelOverrides: nextOverrides,
        },
        externalMetrics,
        internalMetrics,
      );
    });
  };

  const resetMetricLabelOverride = (metricKey) => {
    setMetricLabelOverride(metricKey, "");
  };

  const resetAllMetricLabelOverrides = () => {
    setDraftDisplayConfig((prev) =>
      normalizeDisplayConfig(
        {
          ...prev,
          metricLabelOverrides: {},
        },
        externalMetrics,
        internalMetrics,
      ),
    );
  };

  const setDayLabelOverride = (day, rawValue) => {
    setDraftDisplayConfig((prev) => {
      const nextOverrides = { ...(prev?.dayLabelOverrides || {}) };
      if (typeof rawValue === "string" && rawValue.trim()) {
        nextOverrides[day] = rawValue;
      } else {
        delete nextOverrides[day];
      }
      return normalizeDisplayConfig(
        { ...prev, dayLabelOverrides: nextOverrides },
        externalMetrics,
        internalMetrics,
      );
    });
  };

  const resetAllDayLabelOverrides = () => {
    setDraftDisplayConfig((prev) =>
      normalizeDisplayConfig(
        { ...prev, dayLabelOverrides: {} },
        externalMetrics,
        internalMetrics,
      ),
    );
  };

  const hasCustomMetricLabels = Object.keys(draftDisplayConfig?.metricLabelOverrides || {}).length > 0;
  const hasCustomDayLabels = Object.keys(draftDisplayConfig?.dayLabelOverrides || {}).length > 0;

  const handleSave = () => {
    const normalized = normalizePercentageMatrix(draftMatrix, metrics, draftJDays);
    const normalizedDisplayConfig = normalizeDisplayConfig(
      draftDisplayConfig,
      externalMetrics,
      internalMetrics,
    );
    const normalizedMicrocycle = normalizeMicrocycleConfig(draftMicrocycle);
    const nextSimilarityFrom = normalizedMicrocycle.calculate_similarity_from;

    onSave?.({
      planPercentages: normalized,
      displayConfig: normalizedDisplayConfig,
      microcycle: normalizedMicrocycle,
      calculateSimilarityFromDay: nextSimilarityFrom,
      jDays: draftJDays,
    });
  };

  const updateMicrocycle = (patch) => {
    setDraftMicrocycle((prev) => normalizeMicrocycleConfig({ ...(prev || {}), ...(patch || {}) }));
  };

  const setChartDisplayMode = (mode) => {
    setDraftDisplayConfig((prev) =>
      normalizeDisplayConfig(
        {
          ...prev,
          chartDisplayMode: mode,
        },
        externalMetrics,
        internalMetrics,
      ),
    );
  };

  const toggleMetric = (scope, metricKey) => {
    const field = scope === "internal" ? "internalMetricKeys" : "externalMetricKeys";

    setDraftDisplayConfig((prev) => {
      const current = new Set(prev?.[field] || []);

      if (current.has(metricKey)) {
        current.delete(metricKey);
      } else {
        current.add(metricKey);
      }

      return normalizeDisplayConfig(
        {
          ...prev,
          [field]: Array.from(current),
        },
        externalMetrics,
        internalMetrics,
      );
    });
  };

  const selectAllMetrics = (scope) => {
    const field = scope === "internal" ? "internalMetricKeys" : "externalMetricKeys";
    const sourceMetrics = scope === "internal" ? internalMetrics : externalMetrics;

    setDraftDisplayConfig((prev) =>
      normalizeDisplayConfig(
        {
          ...prev,
          [field]: getMetricKeys(sourceMetrics),
        },
        externalMetrics,
        internalMetrics,
      ),
    );
  };

  const selectNoMetrics = (scope) => {
    const field = scope === "internal" ? "internalMetricKeys" : "externalMetricKeys";
    const sourceMetrics = scope === "internal" ? internalMetrics : externalMetrics;
    const firstKey = sourceMetrics?.[0]?.key;

    setDraftDisplayConfig((prev) =>
      normalizeDisplayConfig(
        {
          ...prev,
          [field]: firstKey ? [firstKey] : [],
        },
        externalMetrics,
        internalMetrics,
      ),
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-3 sm:p-4 backdrop-blur-sm animate-fade-in">
      <div
        className={`my-3 w-full max-w-6xl max-h-[95vh] rounded-2xl border shadow-2xl overflow-hidden flex flex-col ${
          isDarkMode ? "border-gray-800 bg-gray-950 text-gray-100" : "border-gray-200 bg-white text-gray-900"
        }`}
      >
        <div className={`flex items-start justify-between border-b px-6 py-5 ${isDarkMode ? "border-gray-800/80" : "border-gray-200"}`}>
          <div className="space-y-1">
            <h2 className="text-xl font-extrabold tracking-tight">
              {t("weekly_recap.modal.title", { defaultValue: "Customize global expectations" })}
            </h2>
            <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              {t("weekly_recap.modal.subtitle", {
                defaultValue: "Set the expected training load percentages for each day. These apply to each player's individual references.",
              })}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`rounded-xl p-2 transition-colors ${isDarkMode ? "hover:bg-gray-800 text-gray-400 hover:text-gray-100" : "hover:bg-gray-100 text-gray-500"}`}
            aria-label={t("weekly_recap.modal.close", { defaultValue: "Close" })}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {/* Intuitive and helpful Adjustment and MD+N Day subsets segment card */}
          <div className={`px-6 py-4 border-b flex flex-col md:flex-row md:items-center justify-between gap-4 ${isDarkMode ? "bg-gray-900/40 border-gray-800/80" : "bg-gray-50 border-gray-100"}`}>
            <div className="max-w-2xl">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold ${
                isDarkMode ? "bg-cyan-900/30 text-cyan-300 ring-1 ring-cyan-500/20" : "bg-cyan-50 text-cyan-700 ring-1 ring-cyan-600/10"
              }`}>
                {t("weekly_recap.modal.scope_global_badge", { defaultValue: "Configuration globale" })}
              </span>
              <p className={`mt-1.5 text-xs leading-relaxed ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                <strong>{t("weekly_recap.modal.adjustment_title", { defaultValue: "Option d'ajustement" })} :</strong>{" "}
                {t("weekly_recap.modal.adjustment_desc", {
                  defaultValue: "Permet de choisir à partir de quel MD+N la similitude de charge doit être évaluée. Par exemple, si vous choisissez MD+3, les séances de récupération de MD+1 et MD+2 ne seront pas prises en compte dans les calculs.",
                })}
              </p>
            </div>

            <div className={`flex flex-col gap-1.5 p-3 rounded-xl border shrink-0 ${
              isDarkMode ? "bg-gray-950 border-gray-800" : "bg-white border-gray-200"
            }`}>
              <label className="text-xs font-bold uppercase tracking-wider opacity-75">
                {t("weekly_recap.modal.calculate_similarity_from", { defaultValue: "Calculer la similitude à partir de :" })}
              </label>
              <select
                value={draftMicrocycle.calculate_similarity_from}
                onChange={(e) => {
                  updateMicrocycle({ calculate_similarity_from: e.target.value });
                }}
                className={`rounded-lg border px-3 py-1.5 text-xs font-extrabold focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
                  isDarkMode
                    ? "border-gray-700 bg-gray-900 text-cyan-300"
                    : "border-gray-300 bg-gray-50 text-cyan-700"
                }`}
              >
                {draftJDays.map((day) => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={`border-b px-6 py-4 ${isDarkMode ? "border-gray-800/80 bg-gray-900/25" : "border-gray-100 bg-gray-50/70"}`}>
            <div className="mb-3">
              <h3 className="text-xs font-bold uppercase tracking-wider opacity-80">
                {t("weekly_recap.modal.md_position_title", { defaultValue: "MD position" })}
              </h3>
              <p className={`mt-1 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                {t("weekly_recap.modal.md_position_subtitle", {
                  defaultValue: "Choose whether the match day is displayed at the beginning (post-match recovery) or at the end (pre-match preparation) of the microcycle.",
                })}
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
              {[
                {
                  value: MD_POSITION_START,
                  label: t("weekly_recap.modal.md_position_start", { defaultValue: "MD at the start" }),
                  sub: t("weekly_recap.modal.md_position_start_desc", { defaultValue: "MD → MD+6 · post-match recovery" }),
                  preview: ["MD", "MD+1", "MD+2", "MD+3", "MD+4", "MD+5", "MD+6"],
                },
                {
                  value: MD_POSITION_END,
                  label: t("weekly_recap.modal.md_position_end", { defaultValue: "MD at the end" }),
                  sub: t("weekly_recap.modal.md_position_end_desc", { defaultValue: "MD-6 → MD · pre-match preparation" }),
                  preview: ["MD-6", "MD-5", "MD-4", "MD-3", "MD-2", "MD-1", "MD"],
                },
              ].map(({ value, label, sub, preview }) => {
                const active = draftMicrocycle.md_position === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => updateMicrocycle({ md_position: value })}
                    className={`flex flex-1 flex-col items-start gap-2 rounded-xl border p-3 text-left transition-all ${
                      active
                        ? isDarkMode
                          ? "border-cyan-500/50 bg-cyan-950/30 ring-1 ring-cyan-500/30"
                          : "border-cyan-500/40 bg-cyan-50 ring-1 ring-cyan-400/30"
                        : isDarkMode
                          ? "border-gray-700 bg-gray-900/40 hover:border-gray-600"
                          : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <div className="flex w-full items-center gap-2">
                      <span className={`h-3 w-3 rounded-full border-2 shrink-0 ${
                        active
                          ? isDarkMode ? "border-cyan-400 bg-cyan-400" : "border-cyan-600 bg-cyan-600"
                          : isDarkMode ? "border-gray-600 bg-transparent" : "border-gray-300 bg-transparent"
                      }`} />
                      <span className={`text-sm font-bold ${active ? (isDarkMode ? "text-cyan-300" : "text-cyan-700") : ""}`}>
                        {label}
                      </span>
                    </div>
                    <p className={`text-[11px] leading-relaxed ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{sub}</p>
                    <div className="flex flex-wrap gap-1">
                      {preview.map((d) => (
                        <span
                          key={d}
                          className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                            d === "MD"
                              ? isDarkMode ? "bg-indigo-900/50 text-indigo-300" : "bg-indigo-100 text-indigo-700"
                              : isDarkMode ? "bg-gray-800 text-gray-400" : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          {d}
                        </span>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className={`border-b px-6 py-5 ${isDarkMode ? "border-gray-800/80" : "border-gray-100"}`}>
            <div className="mb-4">
              <h3 className="text-sm font-extrabold uppercase tracking-wider">
                {t("weekly_recap.modal.display_title", { defaultValue: "Display configuration" })}
              </h3>
              <p className={`mt-1 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                {t("weekly_recap.modal.display_subtitle", {
                  defaultValue: "Choose the default chart mode and the metrics displayed in external and internal views.",
                })}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className={`rounded-xl border p-3 ${isDarkMode ? "border-gray-800 bg-gray-900/50" : "border-gray-200 bg-gray-50"}`}>
                <div className="mb-2 text-xs font-bold uppercase tracking-wider opacity-75">
                  {t("weekly_recap.modal.default_chart_mode", { defaultValue: "Default chart mode" })}
                </div>

                <div className="inline-flex rounded-lg bg-gray-200 p-0.5 dark:bg-gray-800/80">
                  <button
                    type="button"
                    onClick={() => setChartDisplayMode("absolute")}
                    className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                      draftDisplayConfig.chartDisplayMode !== "percent"
                        ? "bg-white text-cyan-600 shadow-sm dark:bg-gray-700 dark:text-cyan-300"
                        : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    }`}
                  >
                    {t("weekly_recap.controls.absolute", { defaultValue: "Real values" })}
                  </button>

                  <button
                    type="button"
                    onClick={() => setChartDisplayMode("percent")}
                    className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                      draftDisplayConfig.chartDisplayMode === "percent"
                        ? "bg-white text-cyan-600 shadow-sm dark:bg-gray-700 dark:text-cyan-300"
                        : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    }`}
                  >
                    {t("weekly_recap.controls.percentages", { defaultValue: "Percentage (%)" })}
                  </button>
                </div>
              </div>

              <MetricSelector
                title={t("weekly_recap.modal.external_metrics", { defaultValue: "External metrics" })}
                scope="external"
                metrics={externalMetrics}
                selectedKeys={draftDisplayConfig.externalMetricKeys}
                isDarkMode={isDarkMode}
                t={t}
                onToggle={toggleMetric}
                onAll={selectAllMetrics}
                onNone={selectNoMetrics}
                metricLabelOverrides={draftDisplayConfig.metricLabelOverrides}
              />

              <MetricSelector
                title={t("weekly_recap.modal.internal_metrics", { defaultValue: "Internal metrics" })}
                scope="internal"
                metrics={internalMetrics}
                selectedKeys={draftDisplayConfig.internalMetricKeys}
                isDarkMode={isDarkMode}
                t={t}
                onToggle={toggleMetric}
                onAll={selectAllMetrics}
                onNone={selectNoMetrics}
                metricLabelOverrides={draftDisplayConfig.metricLabelOverrides}
              />
            </div>
          </div>

          <div className={`border-b px-6 py-5 ${isDarkMode ? "border-gray-800/80" : "border-gray-100"}`}>
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-extrabold uppercase tracking-wider">
                  {t("weekly_recap.modal.metric_labels_title", { defaultValue: "Metric labels" })}
                </h3>
                <p className={`mt-1 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                  {t("weekly_recap.modal.metric_labels_subtitle", {
                    defaultValue: "Rename metric labels for display only. Computation keys remain unchanged.",
                  })}
                </p>
              </div>

              <button
                type="button"
                onClick={resetAllMetricLabelOverrides}
                disabled={!hasCustomMetricLabels}
                className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                  isDarkMode ? "border-gray-600 hover:bg-gray-800" : "border-gray-300 hover:bg-gray-100"
                }`}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {t("weekly_recap.modal.reset_all_labels", { defaultValue: "Reset all labels" })}
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {metrics.map((metric) => {
                const defaultLabel = getDefaultMetricLabel(metric, t);
                const customLabel = draftDisplayConfig?.metricLabelOverrides?.[metric.key] || "";
                const hasOverride = Boolean(customLabel.trim());

                return (
                  <div
                    key={`label-${metric.key}`}
                    className={`rounded-xl border p-3 ${isDarkMode ? "border-gray-800 bg-gray-900/40" : "border-gray-200 bg-gray-50"}`}
                  >
                    <div className="mb-2 flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-xs font-bold uppercase tracking-wider opacity-70">
                          {defaultLabel}
                        </div>
                        <div className={`mt-0.5 text-[11px] ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                          {t("weekly_recap.modal.default_label", { defaultValue: "Default label" })}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => resetMetricLabelOverride(metric.key)}
                        disabled={!hasOverride}
                        className={`rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-wide transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                          isDarkMode ? "bg-gray-800 text-gray-300 hover:bg-gray-700" : "bg-white text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        {t("weekly_recap.modal.reset_label", { defaultValue: "Default" })}
                      </button>
                    </div>

                    <input
                      type="text"
                      value={customLabel}
                      onChange={(event) => setMetricLabelOverride(metric.key, event.target.value)}
                      placeholder={defaultLabel}
                      className={`w-full rounded-lg border px-3 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
                        isDarkMode ? "border-gray-700 bg-gray-900 text-gray-100" : "border-gray-300 bg-white text-gray-900"
                      }`}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className={`border-b px-6 py-5 ${isDarkMode ? "border-gray-800/80" : "border-gray-100"}`}>
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-extrabold uppercase tracking-wider">
                  {t("weekly_recap.modal.day_labels_title", { defaultValue: "Column labels" })}
                </h3>
                <p className={`mt-1 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                  {t("weekly_recap.modal.day_labels_subtitle", {
                    defaultValue: "Rename MD+N column headers for display only (e.g. MD → J). Max 16 chars.",
                  })}
                </p>
              </div>

              <button
                type="button"
                onClick={resetAllDayLabelOverrides}
                disabled={!hasCustomDayLabels}
                className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                  isDarkMode ? "border-gray-600 hover:bg-gray-800" : "border-gray-300 hover:bg-gray-100"
                }`}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {t("weekly_recap.modal.reset_all_day_labels", { defaultValue: "Reset all labels" })}
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
              {draftJDays.map((day) => {
                const customLabel = draftDisplayConfig?.dayLabelOverrides?.[day] || "";
                return (
                  <div key={`daylabel-${day}`} className={`rounded-xl border p-2.5 ${isDarkMode ? "border-gray-800 bg-gray-900/40" : "border-gray-200 bg-gray-50"}`}>
                    <div className={`mb-1.5 flex items-center justify-between gap-1`}>
                      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                        day === "MD"
                          ? isDarkMode ? "bg-indigo-900/50 text-indigo-300" : "bg-indigo-100 text-indigo-700"
                          : isDarkMode ? "bg-gray-800 text-gray-400" : "bg-gray-100 text-gray-500"
                      }`}>{day}</span>
                      {customLabel.trim() && (
                        <button
                          type="button"
                          onClick={() => setDayLabelOverride(day, "")}
                          className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase transition-colors ${
                            isDarkMode ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"
                          }`}
                        >
                          {t("weekly_recap.modal.reset_label", { defaultValue: "Default" })}
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      maxLength={16}
                      value={customLabel}
                      onChange={(e) => setDayLabelOverride(day, e.target.value)}
                      placeholder={day}
                      className={`w-full rounded-lg border px-2.5 py-1.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
                        isDarkMode ? "border-gray-700 bg-gray-900 text-gray-100" : "border-gray-300 bg-white text-gray-900"
                      }`}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className={`px-6 pt-5 ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}>
            <h3 className="text-sm font-extrabold uppercase tracking-wider">
              {t("weekly_recap.modal.percentages_title", { defaultValue: "Training load percentages" })}
            </h3>
            <p className={`mt-1 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              {t("weekly_recap.modal.percentages_subtitle", {
                defaultValue: "These percentages are used to compute planned values by MD+N day.",
              })}
            </p>
          </div>

          <div className="overflow-x-auto px-6 py-5">
            <table className="min-w-[980px] w-full border-separate border-spacing-y-2">
              <thead>
                <tr>
                  <th className="text-left text-xs uppercase tracking-wide opacity-70">{t("weekly_recap.columns.metric", { defaultValue: "Metric" })}</th>
                  {draftJDays.map((day) => (
                    <th key={day} className="text-center text-xs uppercase tracking-wide opacity-70">
                      {getDayDisplayLabel(day, draftDisplayConfig?.dayLabelOverrides)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {metrics.map((metric) => (
                  <tr
                    key={metric.key}
                    className={`${isDarkMode ? "bg-gray-800/70" : "bg-gray-50"}`}
                  >
                    <td className="rounded-l-xl min-w-[220px] px-4 py-2.5 text-sm font-semibold">
                      {getMetricDisplayLabel(metric, t, draftDisplayConfig.metricLabelOverrides)}
                    </td>
                    {draftJDays.map((day, index) => {
                      const radiusClass = index === draftJDays.length - 1 ? "rounded-r-xl" : "";
                      return (
                        <td key={`${metric.key}-${day}`} className={`px-2 py-2 text-center ${radiusClass}`}>
                          <div className="inline-flex items-center gap-1.5">
                            <input
                              type="number"
                              min={0}
                              max={300}
                              step={1}
                              value={toFiniteNumber(draftMatrix?.[metric.key]?.[day], 0)}
                              onChange={(event) => setCell(metric.key, day, event.target.value)}
                              className={`${inputBase} ${
                                isDarkMode
                                  ? "border-gray-600 bg-gray-900 text-gray-100 focus:ring-indigo-500"
                                  : "border-gray-300 bg-white text-gray-900 focus:ring-indigo-300"
                              }`}
                            />
                            <span className="text-xs font-semibold opacity-70">%</span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className={`flex flex-wrap items-center justify-between gap-2 border-t px-5 py-3 ${isDarkMode ? "border-gray-700 bg-gray-950" : "border-gray-200 bg-white"}`}>
          <button
            type="button"
            onClick={resetDefaults}
            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
              isDarkMode ? "border-gray-600 hover:bg-gray-800" : "border-gray-300 hover:bg-gray-100"
            }`}
          >
            <RotateCcw className="h-4 w-4" />
            {t("weekly_recap.modal.reset", { defaultValue: "Reset" })}
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className={`rounded-lg px-3 py-2 text-sm transition-colors ${
                isDarkMode ? "bg-gray-800 text-gray-200 hover:bg-gray-700" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {t("common.cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm text-white transition-colors hover:bg-indigo-700"
            >
              <Save className="h-4 w-4" />
              {t("common.save", { defaultValue: "Save" })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
